import { ColumnTable } from 'arquero'
import { PlotDatum } from 'components/cell-visualizations/Compare/CompareGroups'
import ContinuousComparisonPlot from 'components/cell-visualizations/Compare/ContinuousComparisonPlot'
import { getColumn } from 'components/cell-visualizations/arrow/arqueroHelpers'
import { CellInfo } from 'components/cell-visualizations/tsv/types'
import { useMemo } from 'react'
import { DifferentialFeaturesComparison, PinnedMorphotype } from 'redux/slices'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { convertMorphotypeIdsToCellIdsFromDataFrame } from './morphotypeListUtils'
import { getComparisonNameForOneSelection, getFeatureName } from './resultItemUtils'

type DistributionComparisonPlotProps = {
  comparison: DifferentialFeaturesComparison
  feature: keyof CellInfo
  width?: number // defaults to 500
  height?: number // defaults to 400
  showTitle?: boolean
}

/**
 * @internal - only exposed for unit testing
 * Memory-efficient plot data generation using DataFrame columns instead of CellInfo[] array
 *
 * @param comparison - The differential features comparison object.
 * @param feature - The name of the feature for to generate a comparison plot
 * @param dataFrame - DataFrame containing all cell data
 * @param morphotypes - Morphotype data to look up
 * @returns An array of plot data items (category, value pairs)
 */
export function getPlotDataForComparisonFromDataFrame(
  comparison: DifferentialFeaturesComparison,
  feature: keyof CellInfo,
  dataFrame: ColumnTable,
  morphotypes: PinnedMorphotype[]
): PlotDatum<number>[] {
  const result: PlotDatum<number>[] = []

  // Extract columns once using cached helper
  const cellIdColumn = getColumn<string>(dataFrame, 'CELL_ID')
  const featureColumn = getColumn<string | number>(dataFrame, feature as string)

  if (cellIdColumn.length === 0 || featureColumn.length === 0) {
    return result
  }

  function addCellIds(morphotypeIds: number[], otherMorphotypeIds: number[]) {
    const cellIdSet = new Set(
      convertMorphotypeIdsToCellIdsFromDataFrame(
        morphotypeIds,
        otherMorphotypeIds,
        dataFrame,
        morphotypes
      )
    )
    const name = getComparisonNameForOneSelection(morphotypeIds, morphotypes)

    // Iterate through DataFrame columns (memory-efficient)
    for (let i = 0; i < cellIdColumn.length; i += 1) {
      const cellId = cellIdColumn[i]
      if (cellId && cellIdSet.has(cellId)) {
        const value = featureColumn[i]
        if (value !== undefined && value !== null && value !== '') {
          result.push({
            category: name,
            value: parseFloat(String(value)),
          })
        }
      }
    }
  }

  addCellIds(comparison.morphotypeIds1, comparison.morphotypeIds2)
  addCellIds(comparison.morphotypeIds2, comparison.morphotypeIds1)

  return result
}

const DistributionComparisonPlot = ({
  comparison,
  feature,
  width = 500,
  height = 400,
  showTitle = true,
}: DistributionComparisonPlotProps): JSX.Element => {
  const { dataFrame, getMergedPinnedMorphotypes } = useCellVisualizationsSlice()
  const morphotypes = getMergedPinnedMorphotypes()
  const divergenceScore = comparison.topFeatures?.find((f) => f.feature === feature)?.score
  const title = showTitle
    ? `${getFeatureName(feature)}<br>Divergence: ${divergenceScore?.toFixed(2)}`
    : undefined

  const plotData = useMemo(
    () => {
      if (!dataFrame) return []
      return getPlotDataForComparisonFromDataFrame(comparison, feature, dataFrame, morphotypes)
    },
    [comparison, feature, dataFrame, morphotypes]
  )

  return (
    <ContinuousComparisonPlot
      key={`distribution-comparison-${feature}`}
      data={plotData}
      title={title}
      titleFontSize={14}
      width={width}
      height={height}
    />
  )
}

export default DistributionComparisonPlot
